import {Button, Input} from 'antd'
import {
    game_createGameQuestion_Api,
    game_deleteGameQuestion_Api,
    game_editGameQuestion_Api,
    game_getById_Api,
    game_getGameQuestions_Api,
} from 'apis'
import {createValue} from 'components/form/utils'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar, {SubmitButton} from 'components/form/FormActionBar'
import {Formik} from 'formik'
import classNames from 'classnames'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {BiLeftArrow, BiSolidEdit, BiTrash} from 'react-icons/bi'
import {HiPlus} from 'react-icons/hi2'
import {TbChevronDown, TbChevronUp, TbEdit, TbTrash} from 'react-icons/tb'
import {NullAsDefault} from 'views/Shared'
import EmptyHolder from 'components/EmptyHolder'
import {GamePuzzleEmptyIcon} from '../../AppLocals/Game/components/GameEmptyIcon'
import {createControlledFormField} from '../../AppLocals/factory/createFormField'
import {SelectWidget} from '../../AppLocals/custom/ReactSelectComponents'
import {notifyOnError} from '../../AppLocals/factory/createErrorEvent'
import {showDeleteConfirmDialog} from '../../AppLocals/factory/createConfirmDialog'
import usePaginationHelper, {createPagerKey} from '../../AppLocals/hooks/usePaginationHelper'
import {bindQueryParam} from '../../AppLocals/functions/routerHelper'
import {GameQuestionOptionsPanel} from './GameQuestionOptionsPanel'
import {BsArrowLeftCircle} from "react-icons/bs";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../helpers/getLinkToDetail";
import {formatSimplifyDate} from "../../../helpers/dateUtils";
import {StudentActionTypes} from "../../AppLocals/Course";
import {GoDotFill} from "react-icons/go";

const ANSWER_TYPES = [
    {value: 'single_choice', label: 'multiple choice'},
    {value: 'crossword', label: 'crossword'},
    {value: 'matching', label: 'matching pairs'},
    {value: 'ordering', label: 'ordering'},
]

const ANSWER_TYPE_LABELS = _.keyBy(ANSWER_TYPES, 'value')

const questionFormSchema = {
    question_text: {
        title: 'question',
        children: [{name: 'question_text', component: createControlledFormField({rows: 2, placeholder: 'question text placeholder', Component: Input.TextArea})}],
    },
    explanation: {
        title: 'quote and meaning',
        children: [{name: 'explanation', component: createControlledFormField({rows: 2, placeholder: 'explanation placeholder', Component: Input.TextArea})}],
    },
    answer_type: {
        title: 'game type',
        children: [{name: 'answer_type', component: ({name, value, onChange, disabled}) => (
            <Translate>
                {(t) => (
                    <SelectWidget
                        name={name}
                        value={value}
                        disabled={disabled}
                        className="w-full"
                        placeholder={t('game type')}
                        onChange={(v) => onChange(createValue(name, v))}
                        options={ANSWER_TYPES.map((o) => ({label: t(o.label), value: o.value}))}
                    />
                )}
            </Translate>
        )}],
    },
}

// point/câu KHÔNG cho sửa tay - luôn theo point_per_question của game (xem GameQuestion::$editable
// ở backend, cùng lý do: GamePlay rút câu ngẫu nhiên không tính điểm, point lệch giữa các câu sẽ
// làm % kết quả tính sai). Chỉ hiển thị tham khảo, đổi thì vào phần cài đặt game.
const PointHint = ({point}) => (
    <Translate>
        {(t) => (
            <div className="flex items-center gap-2">
                <span className="font-semibold text-color-300 uppercase tracking-wide">{t('point')}</span>
                <div className="flex flex-center w-8 h-8 rounded-lg background-100 border border-color-100 font-bold text-sm text-color-200">
                    {point ?? 0}
                </div>
            </div>
        )}
    </Translate>
)

const CreateQuestionForm = ({defaultAnswerType, pointPerQuestion, onSubmit}) => {
    const t = useTranslate()
    return (
        <Formik
            initialValues={{question_text: undefined, explanation: undefined, answer_type: defaultAnswerType}}
            validate={(values) => {
                const errors = {}
                if (_.isEmpty(values.question_text)) errors.question_text = t('required field')
                return errors
            }}
            validateOnMount={true}
            enableReinitialize={true}
            onSubmit={(values) => onSubmit(_.omitBy(values, _.isUndefined))}>
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                    <FieldsFactory formSchema={[questionFormSchema.answer_type]}/>
                    <PointHint point={pointPerQuestion}/>
                </div>
                <FieldsFactory formSchema={[questionFormSchema.question_text]}/>
                <FieldsFactory formSchema={[questionFormSchema.explanation]}/>
                <FormActionBar submitText="create"/>
            </div>
        </Formik>
    )
}

const EditQuestionForm = ({item, onCancel, onSubmit, submitting}) => {
    const t = useTranslate()
    return (
        <Formik
            initialValues={{question_text: item?.question_text, explanation: item?.explanation}}
            validate={(values) => {
                const errors = {}
                if (_.isEmpty(values.question_text)) errors.question_text = t('required field')
                return errors
            }}
            validateOnMount={true}
            onSubmit={(values) => onSubmit(_.omitBy(values, _.isUndefined))}>
            <div className="flex flex-col gap-3">
                <PointHint point={item?.point}/>
                <FieldsFactory formSchema={[questionFormSchema.question_text]}/>
                <FieldsFactory formSchema={[questionFormSchema.explanation]}/>
                <FormActionBar
                    actions={
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-color-300 hover:text-color-000 cursor-pointer" onClick={onCancel}>
                                {t('cancel')}
                            </span>
                            <SubmitButton label="save" loading={submitting}/>
                        </div>
                    }
                />
            </div>
        </Formik>
    )
}

const QuestionRow = ({item, index, onDelete, onRefresh}) => {
    const t = useTranslate()
    const [isExpanded, setIsExpanded] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const {question_text, answer_type, point, status, game_question_options} = item ?? {}
    const isValid = status === 1

    const {handleAsyncAction: onEdit, isLoading: editing} = useAsyncAction({
        apiInfo: game_editGameQuestion_Api,
        onSuccess: () => {
            setIsEditing(false)
            onRefresh()
        },
        onError: notifyOnError(t),
    })

    return (
        <div
            className={classNames(
                'flex flex-col custom-item-render transition-all duration-300',
                isExpanded || isEditing ? '' : ' list-item'
            )}>
            <div className="p-3 flex items-center gap-3">
                <span
                    className={classNames(
                        "flex-shrink-0 w-7 h-7 flex flex-center rounded-full font-bold text-xs",
                        (isExpanded || isEditing) ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-600'
                    )}>
                    {index + 1}
                </span>
                <button
                    type="button" onClick={() => setIsExpanded((v) => !v)} disabled={isEditing}
                    className="flex-1 min-w-0 text-left cursor-pointer">
                    <div className="flex items-center flex-wrap gap-1.5">
                        <span
                            style={{padding: '2px 8px', borderRadius: 999}}
                            className={'font-bold text-2xs tracking-wide flex-shrink-0 ' + (isValid ? 'text-green-600 bg-green-50' : 'text-orange-500 bg-orange-50')}>
                            {isValid ? t('ready') : t('incomplete')}
                        </span>
                        <span className="font-semibold text-2xs text-gray-600 uppercase leading-tight">
                            <GoDotFill size={8} className="mr-1"/>
                            {t(ANSWER_TYPE_LABELS[answer_type]?.label ?? answer_type)}
                        </span>
                        <span className="font-semibold text-2xs text-gray-600 leading-tight">
                            <GoDotFill size={8} className="mr-1"/>
                            {point} {t('points')}
                        </span>
                    </div>
                    {!isEditing && <div className="font-semibold text-sm text-color-000 mt-1 line-clamp-2">{question_text}</div>}
                </button>
                <div
                    style={{ opacity: (isExpanded || isEditing) && 1 }}
                    className="flex items-center gap-0.5 flex-shrink-0 contextIcon">
                    <button
                        type="button"
                        onClick={() => {
                            setIsEditing((v) => !v)
                            setIsExpanded(false)
                        }}
                        className={classNames(
                            "p-2 flex rounded-lg cursor-pointer",
                            isEditing ? 'bg-blue-50 text-blue-500' : 'text-blue-500 hover:text-blue-700'
                        )}>
                        <TbEdit size={16}/>
                    </button>
                    <button
                        type="button" onClick={() => onDelete(item.id)}
                        className="p-2 flex text-primary-500 hover:text-primary cursor-pointer">
                        <TbTrash size={16}/>
                    </button>
                    <button
                        type="button" onClick={() => setIsExpanded((v) => !v)}
                        disabled={isEditing}
                        className={classNames(
                            "p-2 flex rounded-lg cursor-pointer disabled:opacity-30",
                            isExpanded ? 'bg-secondary-50' : 'text-color-400'
                        )}>
                        {isExpanded ? <TbChevronUp size={18} className="text-secondary-500"/> : <TbChevronDown size={18}/>}
                    </button>
                </div>
            </div>
            {isEditing && (
                <div className="px-4 pb-3">
                    <EditQuestionForm
                        item={item}
                        submitting={editing}
                        onCancel={() => setIsEditing(false)}
                        onSubmit={(values) => onEdit(values, {id: item.id})}
                    />
                </div>
            )}
            {isExpanded && !isEditing && (
                <div className="px-3 pb-3">
                    <GameQuestionOptionsPanel question={item} options={game_question_options} onRefresh={onRefresh}/>
                </div>
            )}
        </div>
    )
}

export const GameQuestions = ({game: gameProp, onBack}) => {
    const t = useTranslate()
    const [showCreate, setShowCreate] = useState(false)
    const [refreshKey, setRefreshKey] = useState()

    // gameProp truyền từ Games.js là item rút gọn trong danh sách (GameTransformerBasic - thiếu
    // point_per_question) - phải tự gọi getById lấy full data (GameTransformer) mới có
    // point_per_question để hiển thị/tham chiếu cho form câu hỏi (rule chung, giống Games.js).
    const {response: gameResponse} = useAsync({
        apiInfo: game_getById_Api,
        query: bindQueryParam({id: gameProp?.id}),
        deps: [gameProp?.id],
    })
    const game = _.get(gameResponse, 'data.data') ?? _.get(gameResponse, 'data') ?? gameProp

    const refresh = () => setRefreshKey(Date.now())

    const {handleAsyncAction: onCreate} = useAsyncAction({
        apiInfo: game_createGameQuestion_Api,
        onSuccess: () => {
            setShowCreate(false)
            refresh()
        },
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: game_deleteGameQuestion_Api,
        onSuccess: refresh,
        onError: notifyOnError(t),
    })

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        renderFilter: NullAsDefault,
        AdvancedFilter: NullAsDefault,
        query_params: {keyword: 'workspace-game-question-keyword'},
        withKey: createPagerKey([game?.id, refreshKey]),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParam({id: game?.id}),
            PaginationMeta: NullAsDefault,
            apiInfo: game_getGameQuestions_Api,
            renderItem: (item, index) => (
                <QuestionRow
                    key={item.id ?? index}
                    item={item}
                    index={index}
                    onDelete={(id) => showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete question',
                        message: 'are you sure you want to delete this question',
                        onOk: () => onDelete({}, {id}),
                    })}
                    onRefresh={refresh}
                />
            ),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
            renderEmpty: () => <EmptyHolder icon={GamePuzzleEmptyIcon}/>,
        },
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {searchBar}
                {filterBar}
            </div>
        ),
    })

    return (
        <div className="flex flex-col gap-3">

            {/* Header Khóa học */}
            <div className="mb-6 p-4 background shadow-lg rounded-b-xl transition-all duration-300">
                <button
                    onClick={onBack}
                    className="mb-2 flex items-center text-color-300 hover:text-color-100 tracking-wide cursor-pointer">
                    <BsArrowLeftCircle size={18} className="mr-2"/>
                    {t('back to game list')}
                </button>
                <h1 className="text-2xl font-bold tracking-wide space-x-1 leading-snug">
                    <span className="text-gray-500 text-underline">{t('kristo games')}:</span>
                    <span>{game?.title}</span>
                </h1>
            </div>

            <div className="flex items-center justify-end gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-lg text-gray-700 tracking-wide">{t('question list')}</h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                <Button
                    color="primary" variant="solid"
                    onClick={() => setShowCreate((v) => !v)}
                    className="flex flex-center rounded-lg no-border font-medium leading-tight px-2 md:px-3">
                    {t('add question')}
                </Button>
            </div>

            {showCreate && (
                <div className="p-4 background border border-color-100 shadow-sm rounded-2xl">
                    <span className="block mb-3 text-xs font-bold text-color-300 uppercase tracking-widest">
                        {t('add question')}
                    </span>
                    <CreateQuestionForm
                        defaultAnswerType={game?.primary_answer_type ?? 'single_choice'}
                        pointPerQuestion={game?.point_per_question}
                        onSubmit={(values) => onCreate(values, {id: game?.id})}
                    />
                </div>
            )}

            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

export default GameQuestions
